import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { useFiltersStore } from '@/store/useFiltersStore';
import { getSurveyResponses } from '@/services/survey.service';
import { getAnalyticsMetrics } from '@/services/analytics.service';
import { signOut } from '@/services/firebase';
import type { SurveyResponse, AnalyticsMetrics } from '@/types';
import DashboardHeader from '@/components/Dashboard/DashboardHeader';
import KPICards from '@/components/Dashboard/KPICards';
import FiltersPanel from '@/components/Dashboard/FiltersPanel';
import ResponsesTable from '@/components/Dashboard/ResponsesTable';
import AnalyticsCharts from '@/components/Dashboard/AnalyticsCharts';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, role, signOut: signOutStore } = useAuthStore();
  const { filters } = useFiltersStore();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [_selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [responsesData, metricsData] = await Promise.all([
          getSurveyResponses(filters),
          getAnalyticsMetrics(filters),
        ]);
        setResponses(responsesData);
        setMetrics(metricsData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  const handleSignOut = async () => {
    await signOut();
    await signOutStore();
    navigate('/login', { replace: true });
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-primary-600 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-lg font-semibold text-gray-700">Loading dashboard...</p>
          <p className="mt-2 text-sm text-gray-500">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <DashboardHeader
        user={user}
        role={role}
        onSignOut={handleSignOut}
      />
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {metrics && <KPICards metrics={metrics} />}
        <FiltersPanel />
        {metrics && <AnalyticsCharts metrics={metrics} responses={responses} />}
        <ResponsesTable
          responses={responses}
          loading={loading}
          onSelectResponse={setSelectedResponse}
        />
      </main>
    </div>
  );
};

export default AdminDashboardPage;

